import { useEffect, useMemo, useState } from "react";
import { Button, Card, Cluster, FormField, Grid, Input, Modal, Select, Stack } from "@salesapp/ui";
import { TipHeading, TipKicker } from "./AdminTip";
import { api, ApiError } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface ThreatEvent {
  id: string;
  created_at: string | null;
  kind: string;
  severity: string;
  source_ip: string | null;
  email: string | null;
  path: string | null;
  method: string | null;
  status: string;
}

interface ThreatSummary {
  open: number;
  critical_open: number;
  last_24h: number;
  lockouts_24h: number;
  failed_logins_24h: number;
  csrf_24h: number;
}

interface ThreatsResponse {
  summary: ThreatSummary;
  events: ThreatEvent[];
  total: number;
  archivable: number;
  retention_days: number;
}

const KIND_LABEL: Record<string, string> = {
  login_failed: "Failed sign-in",
  login_lockout: "Account lockout",
  login_failed_ip_not_allowed: "Tenant IP allowlist",
  ip_blocked: "Blocked network",
  country_blocked: "Blocked country",
  rate_limited: "Rate limit",
  csrf_rejected: "CSRF rejected",
  stolen_token: "Stolen session",
  session_elsewhere: "Signed in elsewhere",
  webhook_invalid: "Invalid webhook",
  password_setup_limited: "Password setup limit",
  password_reset_limited: "Password reset limit",
  password_reset_unknown: "Unknown password reset",
  signup_limited: "Signup limit",
  sso_limited: "SSO limit",
  ai_prompt_injection: "Loxley jailbreak attempt",
  ai_indirect_injection: "Loxley injected CRM text",
  ai_sql_injection: "Loxley SQL injection",
  ai_access_denied: "Loxley access refused",
};

const KIND_OPTIONS = Object.entries(KIND_LABEL);

function utcDay(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 86_400_000).toISOString().slice(0, 10);
}

function defaultFromDay(): string {
  return utcDay(-90);
}

function defaultToDay(): string {
  return utcDay(0);
}

function threatsPath(filters: {
  status: string;
  severity: string;
  kind: string;
  ip: string;
  email: string;
  from: string;
  to: string;
}): string {
  const params = new URLSearchParams();
  params.set("status_filter", filters.status);
  params.set("limit", "100");
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.kind) params.set("kind", filters.kind);
  if (filters.ip) params.set("source_ip", filters.ip);
  if (filters.email) params.set("email", filters.email);
  if (filters.from) params.set("created_from", filters.from);
  if (filters.to) params.set("created_to", filters.to);
  return `/operator/threats?${params.toString()}`;
}

export function ThreatsPage() {
  const [data, setData] = useState<ThreatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("open");
  const [severity, setSeverity] = useState("");
  const [kind, setKind] = useState("");
  const [from, setFrom] = useState(defaultFromDay);
  const [to, setTo] = useState(defaultToDay);
  const [ipInput, setIpInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [ip, setIp] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const filters = useMemo(
    () => ({ status, severity, kind, ip, email, from, to }),
    [status, severity, kind, ip, email, from, to],
  );

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setIp(ipInput.trim());
      setEmail(emailInput.trim());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [ipInput, emailInput]);

  async function load() {
    setError(null);
    const row = await api.get<ThreatsResponse>(threatsPath(filters));
    setData(row);
  }

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      setError(null);
      try {
        const row = await api.get<ThreatsResponse>(threatsPath(filters));
        if (!cancelled) setData(row);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Threats could not be loaded.");
        }
      }
    }
    void refresh();
    return () => {
      cancelled = true;
    };
  }, [filters]);

  async function acknowledge(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api.post(`/operator/threats/${id}/ack`, {});
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not acknowledge.");
    } finally {
      setBusy(null);
    }
  }

  async function archiveMatching() {
    setBusy("archive");
    setError(null);
    try {
      await api.post("/operator/threats/archive", {
        status_filter: filters.status,
        severity: filters.severity || null,
        kind: filters.kind || null,
        source_ip: filters.ip || null,
        email: filters.email || null,
        created_from: filters.from || null,
        created_to: filters.to || null,
      });
      setConfirmClear(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not archive events.");
    } finally {
      setBusy(null);
    }
  }

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading…</p>;
  }

  const { summary, events, total, archivable, retention_days } = data;
  const canClear = archivable > 0 && status !== "archived" && busy === null;

  return (
    <Stack gap={5}>
      <Cluster gap={3} align="center">
        <h2>Threat detection</h2>
        <p className="admin-muted">
          Failed sign-ins, lockouts, CSRF blocks, stolen sessions, and rate-limit hits.
          Events stay searchable for {retention_days} days, including after Clear.
        </p>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}

      <Grid columns={3} gap={4}>
        <Kpi
          label="Open threats"
          tip="Events still awaiting acknowledgement. The hint counts open items marked critical."
          value={summary.open}
          hint={`${summary.critical_open} critical`}
        />
        <Kpi
          label="Last 24 hours"
          tip="New threat events in the past day. Lockouts in the hint are account lockouts in that same window."
          value={summary.last_24h}
          hint={`${summary.lockouts_24h} lockouts`}
        />
        <Kpi
          label="Failed sign-ins (24h)"
          tip="Failed login attempts in the past day. CSRF blocks in the hint are rejected state tokens in that window."
          value={summary.failed_logins_24h}
          hint={`${summary.csrf_24h} CSRF blocks`}
        />
      </Grid>

      <Card>
        <Stack gap={4}>
          <Cluster gap={3} align="center" justify="between">
            <TipHeading
              label="Events"
              tip="Filtered threat feed. Acknowledge open rows after review; Clear archives everything matching the filters."
            />
            <p className="admin-muted">
              {events.length} shown of {total} matching
            </p>
          </Cluster>
          <Grid columns={4} gap={3} className="admin-threat-filters">
            <FormField
              label="From (UTC)"
              htmlFor="threat-from"
              tooltip="Inclusive start date for the event list (UTC calendar day)."
            >
              <Input
                id="threat-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
                aria-label="From date"
              />
            </FormField>
            <FormField
              label="To (UTC)"
              htmlFor="threat-to"
              tooltip="Inclusive end date for the event list (UTC calendar day)."
            >
              <Input
                id="threat-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
                aria-label="To date"
              />
            </FormField>
            <FormField
              label="Severity"
              htmlFor="threat-severity"
              tooltip="critical needs immediate review; warning is elevated; info is informational noise."
            >
              <Select
                id="threat-severity"
                aria-label="Filter severity"
                value={severity}
                onChange={(event) => setSeverity(event.target.value)}
              >
                <option value="">Any severity</option>
                <option value="critical">critical</option>
                <option value="warning">warning</option>
                <option value="info">info</option>
              </Select>
            </FormField>
            <FormField
              label="What"
              htmlFor="threat-kind"
              tooltip="Event type — failed sign-in, lockout, CSRF, rate limit, and the other recorded kinds."
            >
              <Select
                id="threat-kind"
                aria-label="Filter event type"
                value={kind}
                onChange={(event) => setKind(event.target.value)}
              >
                <option value="">Any event</option>
                {KIND_OPTIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="IP"
              htmlFor="threat-ip"
              tooltip="Match events by source IP. Debounced as you type."
            >
              <Input
                id="threat-ip"
                value={ipInput}
                onChange={(event) => setIpInput(event.target.value)}
                aria-label="Filter IP"
                placeholder="127.0.0.1"
              />
            </FormField>
            <FormField
              label="Email"
              htmlFor="threat-email"
              tooltip="Match events tied to an email (failed sign-in, lockout, password flows)."
            >
              <Input
                id="threat-email"
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                aria-label="Filter email"
                placeholder="name@example.com"
              />
            </FormField>
            <FormField
              label="Status"
              htmlFor="threat-status"
              tooltip="Open needs review; acknowledged was reviewed; archived was cleared. All includes every status."
            >
              <Select
                id="threat-status"
                aria-label="Filter threats"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="open">Open</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="archived">Archived</option>
                <option value="all">All</option>
              </Select>
            </FormField>
            <FormField
              label="Clear matching"
              tooltip="Archive every event that matches the current filters. They stay searchable under Archived for the retention window."
            >
              <Button
                type="button"
                variant="secondary"
                disabled={!canClear}
                onClick={() => setConfirmClear(true)}
              >
                Clear
              </Button>
            </FormField>
          </Grid>
          <table>
            <thead>
              <tr>
                <th>When (UTC)</th>
                <th>Severity</th>
                <th>What</th>
                <th>IP</th>
                <th>Email</th>
                <th>Path</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={7} className="admin-muted">
                    No matching events.
                  </td>
                </tr>
              )}
              {events.map((row) => (
                <tr key={row.id}>
                  <td>{formatUtc(row.created_at)}</td>
                  <td>
                    <span className={`admin-badge is-${row.severity}`}>{row.severity}</span>
                  </td>
                  <td>{KIND_LABEL[row.kind] ?? row.kind}</td>
                  <td><code>{row.source_ip || "—"}</code></td>
                  <td>{row.email || "—"}</td>
                  <td className="admin-muted">{row.method} {row.path}</td>
                  <td>
                    {row.status === "open" ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busy === row.id}
                        onClick={() => acknowledge(row.id)}
                      >
                        {busy === row.id ? "Saving…" : "Acknowledge"}
                      </Button>
                    ) : (
                      <span className={`admin-badge is-${row.status}`}>
                        {row.status === "archived" ? "Archived" : "Acknowledged"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      </Card>

      <Modal
        isOpen={confirmClear}
        onClose={() => setConfirmClear(false)}
        title="Clear matching events"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={() => setConfirmClear(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy === "archive"}
              onClick={() => archiveMatching()}
            >
              {busy === "archive" ? "Archiving…" : `Archive ${archivable} events`}
            </Button>
          </>
        }
      >
        <p>
          Archive {archivable} matching events? They stay searchable under Archived
          for {retention_days} days.
        </p>
      </Modal>
    </Stack>
  );
}

function Kpi({
  label,
  tip,
  value,
  hint,
}: {
  label: string;
  tip: string;
  value: number;
  hint: string;
}) {
  return (
    <Card>
      <Stack gap={2}>
        <TipKicker label={label} tip={tip} />
        <p className="admin-kpi">{value}</p>
        <p className="admin-muted">{hint}</p>
      </Stack>
    </Card>
  );
}
